// Manejo del foco de un diálogo: qué se puede enfocar dentro, cómo dar la vuelta con Tab y a quién
// devolverle el foco al cerrar. Lógica sin React para poder usarla en `Modal` hoy y en la hoja
// inferior cuando exista.

const CANDIDATES = 'a[href], button, input, select, textarea, [tabindex]';

function isRadio(element: Element | null): element is HTMLInputElement {
  return element instanceof HTMLInputElement && element.type === 'radio';
}

/**
 * ¿Llega Tab a este elemento?
 *
 * `:disabled` y no el atributo `disabled`: un control dentro de un `fieldset` apagado está apagado
 * aunque él no lleve el atributo. Contarlo como parada rompía la trampa — desde el último control
 * real, Tab no daba la vuelta y el navegador sacaba el foco del diálogo.
 */
function isTabStop(element: HTMLElement, hasLayout: boolean): boolean {
  if (element.matches(':disabled')) return false;
  if (element instanceof HTMLInputElement && element.type === 'hidden') return false;
  if (element.tabIndex < 0) return false;
  if (!hasLayout) return true;
  if (element.getClientRects().length === 0) return false;
  // Un elemento con `visibility` distinta de `visible` conserva su caja pero no recibe el foco.
  return getComputedStyle(element).visibility === 'visible';
}

/**
 * Los elementos del contenedor a los que llega Tab, en orden.
 *
 * Un elemento oculto por CSS no cuenta, pero eso solo se puede saber donde hay un motor de
 * maquetación. El propio contenedor sirve de testigo: si un diálogo que está en pantalla no reporta
 * ninguna caja, es que no hay maquetación (jsdom, en las pruebas) y entonces no se filtra por
 * visibilidad en vez de descartarlo todo.
 */
export function tabbableWithin(container: HTMLElement): HTMLElement[] {
  const hasLayout = container.getClientRects().length > 0;
  return Array.from(container.querySelectorAll<HTMLElement>(CANDIDATES)).filter((element) => isTabStop(element, hasLayout));
}

/**
 * Tab recorre un grupo de opciones (radios con el mismo `name`) como UNA sola parada. Para saber si
 * el foco está en la última parada hay que quitar de la lista al resto del grupo del elemento activo.
 */
function stopsAround(elements: HTMLElement[], active: Element | null): HTMLElement[] {
  if (!isRadio(active) || !active.name) return elements;
  return elements.filter((element) => element === active || !isRadio(element) || element.name !== active.name);
}

/** En un grupo de opciones, la parada es la opción marcada (si hay una), igual que hace el navegador. */
function groupStop(container: HTMLElement, element: HTMLElement): HTMLElement {
  if (!isRadio(element) || !element.name) return element;
  const checked = tabbableWithin(container).find((other) => isRadio(other) && other.name === element.name && other.checked);
  return checked ?? element;
}

/**
 * Enfoca el primero de la lista que de verdad acepte el foco. La lista de paradas es una estimación
 * (hay formas de no ser enfocable que no se ven desde aquí); si un candidato no lo toma se prueba el
 * siguiente, y si ninguno, el foco se queda en el contenedor. Nunca se queda fuera.
 */
function focusFirstThatSticks(candidates: HTMLElement[], container: HTMLElement): void {
  for (const candidate of candidates) {
    candidate.focus({ preventScroll: true });
    if (document.activeElement === candidate) return;
  }
  container.focus({ preventScroll: true });
}

/**
 * Mantiene Tab dentro del diálogo. Devuelve `true` si movió el foco (y entonces quien llama debe
 * cancelar el Tab del navegador); `false` si el navegador puede seguir su camino normal.
 */
export function keepTabInside(container: HTMLElement, backwards: boolean): boolean {
  const active = document.activeElement;
  const stops = stopsAround(tabbableWithin(container), active);

  // Nada que enfocar dentro (por ejemplo, todo apagado mientras el diálogo está ocupado).
  if (stops.length === 0) {
    container.focus({ preventScroll: true });
    return true;
  }

  const first = groupStop(container, stops[0]!);
  const last = groupStop(container, stops[stops.length - 1]!);
  const fromStart = [first, ...stops.slice(1)];
  const fromEnd = [last, ...stops.slice(0, -1).reverse()];

  // El foco está fuera, o en el propio contenedor: se entra por el extremo que corresponde.
  const inside = active instanceof HTMLElement && active !== container && container.contains(active);
  if (!inside) {
    focusFirstThatSticks(backwards ? fromEnd : fromStart, container);
    return true;
  }
  if (backwards && active === first) {
    focusFirstThatSticks(fromEnd, container);
    return true;
  }
  if (!backwards && active === last) {
    focusFirstThatSticks(fromStart, container);
    return true;
  }
  return false;
}

/**
 * ¿Se le puede devolver el foco a este elemento al cerrar el diálogo?
 *
 * No a un campo de texto: en el mostrador, devolverle el foco volvería a levantar el teclado en
 * pantalla sobre lo que la persona quiere ver. `PENDIENTE DE PRUEBA` en el equipo — mientras no se
 * mida, se elige lo que no estorba.
 */
export function canReceiveReturnedFocus(element: Element | null): element is HTMLElement {
  if (!(element instanceof HTMLElement) || !element.isConnected || element === document.body) return false;
  if (element instanceof HTMLTextAreaElement || element.isContentEditable) return false;
  if (element instanceof HTMLInputElement) return ['button', 'submit', 'reset', 'checkbox', 'radio'].includes(element.type);
  return true;
}
